<template>
    <div
        class="flex flex-col -mt-6 sm:-mt-20"
        :class="
            isImpersonator
                ? 'h-[calc(100dvh-128px)] sm:h-[calc(100dvh-144px)]'
                : 'h-[calc(100dvh-64px)] sm:h-[calc(100dvh-80px)]'
        "
    >
        <message-hub-navbar v-if="!isMdShortHeight"></message-hub-navbar>

        <div
            :ref="(el) => (sidebarPane.containerRef.value = el)"
            class="relative flex h-full overflow-hidden gap-6 md:pb-3 md:px-6 md:pt-6 md:flex-row"
        >
            <div
                :ref="(el) => (sidebarPane.panelRef.value = el)"
                class="flex flex-col grow lg:max-w-xl 2xl:max-w-2xl min-w-0"
                :class="sidebarClasses"
                :style="sidebarPane.panelStyle.value"
            >
                <message-hub-navbar v-if="isMdShortHeight"></message-hub-navbar>
                <intelligent-inbox-sidebar v-if="isIntelligentRoute"></intelligent-inbox-sidebar>
                <message-hub-scheduled-messages-sidebar
                    v-else-if="isScheduledRoute"
                ></message-hub-scheduled-messages-sidebar>
                <message-hub-threads-sidebar v-else></message-hub-threads-sidebar>
            </div>

            <message-hub-sidebar-resize-handle
                :enabled="sidebarResizeEnabled"
                :isResizing="sidebarPane.isResizing.value"
                :handleStyle="sidebarPane.handleStyle.value"
                @start="sidebarPane.startResize"
            ></message-hub-sidebar-resize-handle>

            <router-view
                class="grow"
                :class="[
                    isSidebarLanding ? 'hidden md:flex' : '',
                    route.name === 'message-hub.contact-view' ? 'md:w-2/3' : 'md:w-1/2',
                ]"
            ></router-view>
        </div>
    </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, watch } from 'vue';
import { RouterView, useRoute } from 'vue-router';
import MessageHubNavbar from '~/components/message-hub/MessageHubNavbar.vue';
import MessageHubSidebarResizeHandle from '~/components/message-hub/MessageHubSidebarResizeHandle.vue';
import { useMessageHub } from '~/components/message-hub/useMessageHub';
import { useResizablePane } from '~/components/message-hub/useResizablePane';
import { useResponsiveLayout } from '~/composables/useResponsiveLayout';
import MessageHubThreadsSidebar from '~/components/message-hub/message-hub-threads/MessageHubThreadsSidebar.vue';
import MessageHubScheduledMessagesSidebar from '~/components/message-hub/message-hub-scheduled-messages/MessageHubScheduledMessagesSidebar.vue';
import IntelligentInboxSidebar from '~/components/message-hub/intelligent-inbox/IntelligentInboxSidebar.vue';
import { pendoEvent, trackPendoEvent } from '~/utils/pendo';
import { useCurrentGroup } from '~/composables/useCurrentGroup';

const route = useRoute();
const isImpersonator = Spark.isImpersonating;
const { isLg, isMd, isMdShortHeight } = useResponsiveLayout();

const { getCurrentGroupId } = useCurrentGroup();

const { mountMessageHub, currentInbox, isInboxRoute, isScheduledRoute, isIntelligentRoute, clearSelection } =
    useMessageHub();

watch(currentInbox, (inbox) => {
    if (!isInboxRoute.value && !isIntelligentRoute.value) {
        return;
    }
    trackPendoEvent(
        pendoEvent('Message Hub Inbox Navigation', {
            inbox,
            team_id: Spark.state.currentTeam.id,
            group_id: getCurrentGroupId(),
        })
    );
});

onMounted(() => {
    mountMessageHub();

    document.body.classList.add('overflow-hidden');
});

onUnmounted(() => {
    document.body.classList.remove('overflow-hidden');
    clearSelection();
});

const isSidebarLanding = computed(() => {
    return route.name === 'message-hub' || route.name === 'message-hub.scheduled-messages';
});

const sidebarResizeEnabled = computed(() => {
    return isMd.value && (route.name !== 'message-hub.contact-view' || isLg.value);
});

const CONVERSATION_MIN_WIDTH_PX = 420;
const CONTACT_PANE_MIN_WIDTH_PX = 260;
const CONVERSATION_INNER_GAP_PX = 12;

const sidebarMinCompanionWidth = computed(() => {
    if (route.name === 'message-hub.contact-view') {
        return CONVERSATION_MIN_WIDTH_PX + CONVERSATION_INNER_GAP_PX + CONTACT_PANE_MIN_WIDTH_PX;
    }
    return CONVERSATION_MIN_WIDTH_PX;
});

const sidebarPane = useResizablePane({
    enabled: sidebarResizeEnabled,
    storageKey: 'voxie.messageHub.sidebarWidth',
    defaultWidth: 448,
    minWidth: 324,
    minCompanionWidth: sidebarMinCompanionWidth,
});

const sidebarClasses = computed(() => {
    if (route.name === 'message-hub.contact-view') {
        return 'hidden lg:flex w-1/3';
    }

    return isSidebarLanding.value ? 'flex w-1/2' : 'hidden md:flex w-1/2';
});
</script>
